import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Fragment } from 'react';
import { FieldValues, useController } from 'react-hook-form';

import { AutocompleteField } from 'common/forms/Field/AutocompleteField';
import { Checkbox } from 'common/components/Checkbox';
import { Divider } from 'common/components/Divider';
import { TControllerProps } from 'common/types';
import { asTypedControl } from 'common/utils/form';

interface IProps extends Omit<
    React.ComponentProps<typeof AutocompleteField>,
    'name'
> {
    name: string;
    placeholder?: string;
    getOptionKey?(option: unknown): string;
    getOptionDisplay?(option: unknown): React.ReactNode;
    getOptionIsInfo?(option: unknown): boolean; // Returns if option is an info option
    getOptionDivider?(option: unknown): boolean; // Returns if option has a divider
}

export const MultiSelectField = <TFieldValues extends FieldValues>({
    control: formControl,
    name,
    getOptionKey,
    getOptionDisplay,
    getOptionIsInfo,
    getOptionDivider,
    ...other
}: IProps & TControllerProps<TFieldValues>) => {
    const control = asTypedControl<string[] | undefined>(name, formControl);

    const {
        field,
        fieldState: { error },
    } = useController({ name, control });
    const { value } = field;
    let { placeholder } = other;

    placeholder = !!placeholder && !value?.length ? placeholder : undefined;

    const inputLabelProps = !!placeholder ? { shrink: true } : undefined;

    return (
        <AutocompleteField
            {...field}
            error={!!error}
            helperText={error?.message}
            renderOption={(renderProps, option, state) => {
                const { key, ...optionProps } = renderProps;
                const optionKey = getOptionKey?.(option);

                // If option is info show a custom greyed out text
                // that is not clickable
                if (getOptionIsInfo?.(option)) {
                    return (
                        <Box
                            component="li"
                            key={optionKey || key}
                            {...optionProps}
                            sx={{
                                pointerEvents: 'none',
                                opacity: 0.6,
                            }}
                        >
                            <Typography
                                sx={{ width: '100%', textAlign: 'center' }}
                                variant="caption"
                            >
                                {getOptionDisplay?.(option)}
                            </Typography>
                        </Box>
                    );
                }

                let optionDisplay = option;

                if (getOptionDisplay) {
                    optionDisplay = getOptionDisplay(option);
                } else if (other.getOptionLabel) {
                    optionDisplay = other.getOptionLabel(option);
                }

                const hasDivider = getOptionDivider?.(option);

                return (
                    <Fragment key={optionKey || key}>
                        <Box component="li" {...optionProps}>
                            <Checkbox
                                checked={state.selected}
                                sx={{ ml: '-9px' }}
                            />

                            <Box
                                sx={{
                                    ml: 0.75,
                                    py: 0.5,
                                    lineHeight: 1.2,
                                    overflow: 'hidden',
                                }}
                            >
                                {optionDisplay as React.ReactNode}
                            </Box>
                        </Box>

                        {hasDivider && <Divider sx={{ my: 1 }} />}
                    </Fragment>
                );
            }}
            {...other}
            disableCloseOnSelect
            multiple
            InputLabelProps={inputLabelProps}
            InputProps={{
                ...other.InputProps,
                placeholder,
                variant: 'outlined',
            }}
            slotProps={{
                chip: {
                    color: 'primary',
                },
            }}
            onChange={(_event, value) => {
                field.onChange(value);
            }}
        />
    );
};
